import { AdvertisementType, type Cryptor } from "@cryptors/api-client";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import { LoadingButton } from "@mui/lab";
import { Typography, Stack, Box, Divider } from "@mui/material";
import { useShow, IResourceComponentsProps } from "@refinedev/core";
import {
  Show,
  NumberField,
  TextFieldComponent as TextField,
  DateField,
  BooleanField,
} from "@refinedev/mui";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import SelectLanguage from "@/components/miscellaneous/SelectLanguage";
import i18n from "@/i18n";
import { getTranslation } from "@/utils/translation";

import CaptureLinkDialog from "./components/CaptureLinkDialog";
import CryptorAddresses from "./components/CryptorAddresses";
import CryptorArtefactShow from "./components/CryptorArtefactShow";

export const CryptorShow: React.FC<IResourceComponentsProps> = () => {
  const navigate = useNavigate();
  const [captureLinkOpen, setCaptureLinkOpen] = useState(false);
  const {
    query: { data, isLoading },
  } = useShow();

  const record = data?.data as Cryptor;
  const cryptorId = data?.data.id;

  return (
    <Show
      isLoading={isLoading}
      headerButtons={({ defaultButtons }) => (
        <Box display="flex" alignItems="center" gap={1}>
          <LoadingButton
            onClick={() => navigate(`/questions/show?cryptorId=${cryptorId}`)}
          >
            <Box display="flex" gap={0.5}>
              <PersonSearchIcon />
              Questions post capture
            </Box>
          </LoadingButton>
          <LoadingButton
            onClick={() => navigate(`/hints/show?cryptorId=${cryptorId}`)}
          >
            <Box display="flex" gap={0.5}>
              <PersonSearchIcon />
              Indices
            </Box>
          </LoadingButton>
          <LoadingButton
            disabled={!cryptorId}
            onClick={() => setCaptureLinkOpen(true)}
          >
            <Box display="flex" gap={0.5}>
              <QrCode2Icon />
              Lien de capture
            </Box>
          </LoadingButton>
          <Divider orientation="vertical" flexItem />
          {defaultButtons}
          <SelectLanguage />
        </Box>
      )}
    >
      {cryptorId !== undefined && (
        <CaptureLinkDialog
          open={captureLinkOpen}
          onClose={() => setCaptureLinkOpen(false)}
          cryptorId={Number(cryptorId)}
        />
      )}
      <Stack gap={1}>
        <Typography variant="body1" fontWeight="bold">
          Id
        </Typography>
        <NumberField value={record?.id ?? ""} />
        <Typography variant="body1" fontWeight="bold">
          Nom
        </Typography>
        <TextField value={record?.name} />
        <Typography variant="body1" fontWeight="bold">
          Date de naissance
        </Typography>
        {record?.birthDate && <DateField value={record?.birthDate} />}
        {record?.birthDateRaw && <TextField value={record?.birthDateRaw} />}
        <Typography variant="body1" fontWeight="bold">
          Date de décès
        </Typography>
        <DateField value={record?.deathDate} />
        <Typography variant="body1" fontWeight="bold">
          Place of birth
        </Typography>
        <TextField value={record?.placeOfBirth} />
        <Typography variant="body1" fontWeight="bold">
          Titre
        </Typography>
        <TextField
          value={getTranslation(record?.titleTranslation, i18n.language)?.value}
        />
        <Typography variant="body1" fontWeight="bold">
          Lien avec le lieu
        </Typography>
        <TextField
          value={
            getTranslation(record?.linkWithThePlaceTranslation, i18n.language)
              ?.value
          }
        />
        <Typography variant="body1" fontWeight="bold">
          Quote
        </Typography>
        <TextField
          value={getTranslation(record?.quoteTranslation, i18n.language)?.value}
        />
        <Typography variant="body1" fontWeight="bold">
          Description
        </Typography>
        <TextField
          value={
            getTranslation(record?.descriptionTranslation, i18n.language)?.value
          }
        />
        <Typography variant="body1" fontWeight="bold">
          Difficulté
        </Typography>
        <TextField
          value={
            getTranslation(record?.difficultyTranslation, i18n.language)?.value
          }
        />

        <Box display="flex" flexDirection="row" gap={3}>
          <Box>
            <Typography variant="body1" fontWeight="bold">
              Image
            </Typography>
            <img src={record?.image?.url} alt="Cryptor" width="100" />
          </Box>
          <Box>
            <Typography variant="body1" fontWeight="bold">
              Icône carte
            </Typography>
            {record?.mapIcon ? (
              <img src={record.mapIcon.url} alt="Icône carte" width="100" />
            ) : (
              <Typography variant="body2">Non définie</Typography>
            )}
          </Box>
          <Box>
            <Typography variant="body1" fontWeight="bold">
              Image (dissimulée)
            </Typography>
            <img src={record?.obfuscatedImage?.url} alt="Cryptor" width="100" />
          </Box>
          <Box>
            <Typography variant="body1" fontWeight="bold">
              Avatar
            </Typography>
            <img src={record?.avatar?.image?.url} alt="Cryptor" width="100" />
          </Box>
          <Box>
            <Typography variant="body1" fontWeight="bold">
              Image du personnage
            </Typography>
            <img src={record?.characterImage?.url} alt="Cryptor" width="100" />
          </Box>
        </Box>

        <Typography variant="body1" fontWeight="bold">
          Rayon de localisation (en mètres)
        </Typography>
        <Typography variant="body1" fontWeight="bold">
          Récompense
        </Typography>
        <NumberField value={record?.reward ?? ""} />
        <Typography variant="body1" fontWeight="bold">
          Couleur primaire
        </Typography>
        <TextField value={record?.primaryColor} />
        <Typography variant="body1" fontWeight="bold">
          Couleur secondaire
        </Typography>
        <TextField value={record?.secondaryColor} />
        <Typography variant="body1" fontWeight="bold">
          Restrictions d'horaire
        </Typography>
        <BooleanField value={record?.timeRestriction} />
        {record?.timeRestriction && (
          <>
            <Typography variant="body1" fontWeight="bold">
              URL de la page de restrictions d'horaire
            </Typography>
            <TextField value={record?.timeRestrictionUrl || "N/A"} />
          </>
        )}
        <Typography variant="body1" fontWeight="bold">
          Maintenance en cours
        </Typography>
        <BooleanField value={record?.underMaintenance} />

        <CryptorArtefactShow
          title="Artefact (lien avec le lieu)"
          artefact={record?.artefactLinkWithThePlace}
        />
        <CryptorArtefactShow
          title="Artefact (description)"
          artefact={record?.artefactDescription}
        />
        <CryptorArtefactShow
          title="Artefact (citation)"
          artefact={record?.artefactQuote}
        />

        {(
          [
            {
              type: AdvertisementType.STORY,
              label: "Publicités (histoire / quiz)",
            },
            {
              type: AdvertisementType.DISCUSSION,
              label: "Publicités (discussion IA)",
            },
          ] as const
        ).map(({ type, label }) => (
          <Box key={type}>
            <Typography variant="body1" fontWeight="bold">
              {label}
            </Typography>
            <Stack gap={1}>
              {record?.advertisements
                ?.filter((link) => link.type === type)
                .map((link, index) => (
                  <Box
                    key={link.advertisement.id}
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <Typography fontWeight="bold">{index + 1}.</Typography>
                    <img
                      src={link.advertisement.image.url}
                      alt={link.advertisement.title}
                      width="60"
                    />
                    <Typography>{link.advertisement.title}</Typography>
                  </Box>
                ))}
            </Stack>
          </Box>
        ))}

        <CryptorAddresses data={record?.addresses} />

        <Typography variant="body1" fontWeight="bold">
          Date de création
        </Typography>
        <DateField value={record?.createdAt} />
        <Typography variant="body1" fontWeight="bold">
          Description IA
        </Typography>
        <TextField value={record?.descriptionForAI} />
        <Typography variant="body1" fontWeight="bold">
          Agent Mistral ID FR
        </Typography>
        <TextField value={record?.mistralAIAgentIdFR} />
        <Typography variant="body1" fontWeight="bold">
          Agent Mistral ID EN
        </Typography>
        <TextField value={record?.mistralAIAgentIdEN} />
        <Typography variant="body1" fontWeight="bold">
          Agent Mistral ID ES
        </Typography>
        <TextField value={record?.mistralAIAgentIdES} />
        <Typography variant="body1" fontWeight="bold">
          Agent Mistral ID DE
        </Typography>
        <TextField value={record?.mistralAIAgentIdDE} />
        <Typography variant="body1" fontWeight="bold">
          Agent Mistral ID HU
        </Typography>
        <TextField value={record?.mistralAIAgentIdHU} />
        <Typography variant="body1" fontWeight="bold">
          Agent Mistral ID IT
        </Typography>
        <TextField value={record?.mistralAIAgentIdIT} />
        <Typography variant="body1" fontWeight="bold">
          Agent Mistral ID NL
        </Typography>
        <TextField value={record?.mistralAIAgentIdNL} />
        <Typography variant="body1" fontWeight="bold">
          Agent Mistral ID PL
        </Typography>
        <TextField value={record?.mistralAIAgentIdPL} />
        <Typography variant="body1" fontWeight="bold">
          Agent Mistral ID PT
        </Typography>
        <TextField value={record?.mistralAIAgentIdPT} />
        <Typography variant="body1" fontWeight="bold">
          Animer les yeux
        </Typography>
        <BooleanField value={record?.shouldAnimateEyes} />
      </Stack>
    </Show>
  );
};
